<script setup>
import { reactive, computed, getCurrentInstance } from 'vue';
import AttachmentDropzone from '../../common/AttachmentDropzone.vue';
import Button from "../../common/Button.vue";

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const state = reactive({
    tableName: props.options.tableName,
    productId: props.options.productId,
    notices: [],
    warnings: [],
    errors: [],
    loading: false,
    rowCount: 0,
    fileName: undefined,
    ignoreErrorsAndWarnings: false
});

// --- Computed ---

const importBtnDisabled = computed(() => {
    if (state.errors.length || state.warnings.length) {
        return !state.ignoreErrorsAndWarnings;
    }
    return false;
});

// --- Methods ---

const resetResults = () => {
    state.loading = false;
    state.notices = [];
    state.warnings = [];
    state.errors = [];
    state.rowCount = 0;
};

const setFile = async (obj) => {
    if (!obj) return;

    resetResults();
    state.loading = true;
    state.fileName = obj.fileName;

    try {
        const response = await store.dispatch('request/post', {
            url: `${store.state.apiEndpoint}/api/import/validate`,
            postData: {
                tableName: state.tableName,
                fileName: state.fileName
            }
        });

        const { notices, warnings, errors, rowCount } = response.data;

        state.notices = notices || [];
        state.warnings = warnings || [];
        state.errors = errors || [];
        state.rowCount = rowCount || 0;

    } catch (err) {
        state.errors = ['An error occurred during validation.'];
    } finally {
        state.loading = false;
    }
};

const importData = async () => {
    // 1. Close modal immediately to show background progress
    emit('hide-modal');

    // 2. Find component title for the notification
    const component = store.state.components.items.find(o => o.tableName === state.tableName);
    const title = component?.title || 'Data';

    // 3. Register background notification
    const { notificationId } = await store.dispatch('toasts/notification', {
        title: `${title} import`,
        body: 'Starting import...',
        closeOnComplete: true
    });

    // 4. Trigger actual import
    try {
        await store.dispatch('request/post', {
            url: `${store.state.apiEndpoint}/api/import/import`,
            postData: {
                tableName: state.tableName,
                fileName: state.fileName,
                productId: state.productId,
                notificationId
            }
        });

        // 5. Refresh UI
        store.commit('cacheNeedsClearing');
        const viewId = store.state.mainViewId;
        if (viewId) {
            await store.dispatch(`${viewId}/scheduleLoad`);
        }
    } catch (err) {
        console.error("Import failed", err);
    }
};
</script>

<template>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Import CSV</h4>
                <button type="button" class="btn-close" @click="$emit('hide-modal')" aria-label="Close"></button>
            </div>

            <div class="modal-body">
                <AttachmentDropzone :set-file="setFile" class="mb-3" />

                <div v-if="state.loading" class="mb-3 text-center">
                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    Processing data&hellip;
                </div>

                <template v-for="(notice, index) in state.notices">
                    <div v-if="notice.heading" :key="'n-card-' + index" class="mb-3 card">
                        <div class="card-header bg-success-subtle text-success-emphasis d-flex align-items-center">
                            <i class="bi bi-info-circle-fill me-2"></i>
                            <span v-html="notice.heading"></span>
                        </div>
                        <div class="card-body" v-html="notice.message" />
                    </div>
                    <div v-else :key="'n-alert-' + index" class="alert alert-success" v-html="notice" />
                </template>

                <template v-for="(warning, index) in state.warnings">
                    <div v-if="warning.heading" :key="'w-card-' + index" class="mb-3 card">
                        <div class="card-header bg-warning-subtle text-warning-emphasis d-flex align-items-center">
                            <i class="bi bi-exclamation-triangle-fill me-2"></i>
                            <span v-html="warning.heading"></span>
                        </div>
                        <div class="card-body" v-html="warning.message" />
                    </div>
                    <div v-else :key="'w-alert-' + index" class="alert alert-warning" v-html="warning" />
                </template>

                <template v-for="(error, index) in state.errors">
                    <div v-if="error.heading" :key="'e-card-' + index" class="mb-3 card">
                        <div class="card-header bg-danger-subtle text-danger-emphasis d-flex align-items-center">
                            <i class="bi bi-exclamation-octagon-fill me-2"></i>
                            <span v-html="error.heading"></span>
                        </div>
                        <div class="card-body" v-html="error.message" />
                    </div>
                    <div v-else :key="'e-alert-' + index" class="alert alert-danger d-flex align-items-center">
                        <i class="bi bi-exclamation-octagon-fill me-2"></i>
                        <span v-html="error"></span>
                    </div>
                </template>

                <div v-if="state.rowCount" class="mt-4 text-center border-top pt-4">
                    <p class="fs-5"><b>{{ state.rowCount }} rows</b> ready to import</p>

                    <p v-if="state.errors.length || state.warnings.length" class="mb-3">
                        <label class="form-check-label" role="button">
                            <input type="checkbox" class="form-check-input" v-model="state.ignoreErrorsAndWarnings">
                            Ignore errors and warnings
                        </label>
                    </p>

                    <Button
                        :disabled="importBtnDisabled"
                        @click.native="importData"
                        :default-class="'btn-warning btn-lg px-5'"
                    >
                        Import
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>